import { Dropdown, Flex, Stack, Text, useDropdownState } from '@vtex/admin-ui'

import { FormattedMessage, useIntl } from 'react-intl'

import { useMemo } from 'react'
import { Event, OPERATORS, SellerOperator, SellerRestriction } from 'typings'
import { useAnalytics } from 'use-analytics'
import { useFormStateContext } from '../../../providers'
import { Tag } from '../../Tag'
import { messages } from './messages'
import { OptIn } from './OptIn'
import { OptInConfirmationModal } from './OptIn/ConfirmationModal'
import { useOptInStateControl } from './OptIn/hooks/useOptInStateControl'
import { styles } from './OptIn/styles'
import { SellersSelector } from './SellersSelector'

type Props = {
  name: string
}

export type SellerOption = {
  id: SellerOperator
  label: JSX.Element
}

const operatorBaseOptions = [
  {
    id: SellerOperator.ALL,
    label: <FormattedMessage {...messages.options.all} />,
  },
  {
    id: SellerOperator.EQUALS,
    label: <FormattedMessage {...messages.options.equals} />,
  },
  {
    id: SellerOperator.DIFFERENT,
    label: <FormattedMessage {...messages.options.different} />,
  },
]

const showSellerSelector = (operator: string) =>
  operator === SellerOperator.DIFFERENT || operator === SellerOperator.EQUALS

export function Sellers(props: Props) {
  const { name } = props
  const { $t } = useIntl()
  const { form } = useFormStateContext()

  const { track } = useAnalytics()

  const {
    sellerRestriction,
    optinEnabled,
    optInConfirmationModal,
    handleOptInSelection,
    isOptInSelection,
    cancelOptInChange,
    confirmOptInChange,
    showOptInCounter,
  } = useOptInStateControl({ name })

  const operatorOptions: SellerOption[] = useMemo(
    () =>
      optinEnabled
        ? [
            ...operatorBaseOptions,
            {
              id: SellerOperator.OPTIN,
              label: <FormattedMessage {...messages.options.optIn} />,
            },
          ]
        : operatorBaseOptions,
    [optinEnabled]
  )

  const currentOperator = useMemo(
    () => sellerRestriction?.operator ?? SellerOperator.ALL,
    [sellerRestriction?.operator]
  )

  const handleEqualsOrDifferentSelection = (
    selectedValue: SellerOperator | undefined,
    previousOption: SellerRestriction | undefined
  ) => {
    form.setValue(name, {
      operator: selectedValue,
      items: previousOption?.items ?? [],
    })
  }

  const handleAllSelection = () => {
    form.setValue(name, undefined)
  }

  const isEqualsOrDifferentSelection = (
    selectedValue: SellerOperator | undefined
  ): boolean =>
    selectedValue === SellerOperator.EQUALS ||
    selectedValue === SellerOperator.DIFFERENT

  const operatorState = useDropdownState({
    items: operatorOptions,
    itemToString: (item) => item?.id ?? '',
    initialSelectedItem: operatorOptions.find((o) => o.id === currentOperator),
    onSelectedItemChange: (event) => {
      const selectedValue = event.selectedItem?.id
      const previousOption: SellerRestriction | undefined = form.getValues(name)

      track(Event.SELLER_SELECTION_CHANGED, {
        from: previousOption?.operator || OPERATORS.ALL,
        to: selectedValue,
      })

      if (optinEnabled && isOptInSelection(selectedValue, previousOption)) {
        handleOptInSelection(selectedValue, previousOption)
      } else if (isEqualsOrDifferentSelection(selectedValue)) {
        handleEqualsOrDifferentSelection(selectedValue, previousOption)
      } else {
        handleAllSelection()
      }
    },
  })

  return (
    <Stack space="$space-5" fluid>
      <Flex align="center" justify="space-between">
        <Text variant="title1">
          <FormattedMessage {...messages.title} />
          {showOptInCounter && (
            <Tag className={styles.counter}>
              {sellerRestriction?.items.length}
            </Tag>
          )}
        </Text>
        <Dropdown
          items={operatorOptions}
          state={operatorState}
          label={$t(messages.applyTo)}
          variant="neutralTertiary"
          renderItem={(item) => item?.label ?? ''}
        />
      </Flex>
      {showSellerSelector(currentOperator) && (
        <SellersSelector name={`${name}.items`} />
      )}
      {optinEnabled && (
        <>
          <OptIn form={form} name={name} />
          <OptInConfirmationModal
            onConfirm={confirmOptInChange}
            onCancel={() => cancelOptInChange(operatorOptions, operatorState)}
            state={optInConfirmationModal}
          />
        </>
      )}
    </Stack>
  )
}
